/**
 * Shared `update_plan` schema — the single source of truth for plan shape.
 *
 * The Hub tool registry builds the AI SDK tool from this zod schema, and the
 * chat UI (PlanCard) parses tool input with it, so the tool contract and the
 * renderer can never drift apart. All plan types are z.infer-derived.
 */

import { z } from 'zod';

/** Const-union of plan step statuses (enum-style, single definition). */
export const PLAN_STEP_STATUSES = ['pending', 'in_progress', 'completed'] as const;
export type PlanStepStatus = (typeof PLAN_STEP_STATUSES)[number];

export const planStepSchema = z.object({
  label: z
    .string()
    .min(1)
    .max(200)
    .describe("Short imperative description of the step, e.g. 'Pull company record and open deals'"),
  status: z.enum(PLAN_STEP_STATUSES).describe('Current status of this step'),
});

export const updatePlanInputSchema = z.object({
  title: z
    .string()
    .max(120)
    .optional()
    .describe("Short title for the overall piece of work, e.g. 'Prep the ACME account brief'"),
  steps: z
    .array(planStepSchema)
    .min(2)
    .max(12)
    .describe(
      'The FULL current step list (not a delta). Re-send every step with updated statuses each time anything changes.'
    ),
});

export type PlanStep = z.infer<typeof planStepSchema>;
export type UpdatePlanInput = z.infer<typeof updatePlanInputSchema>;

/** Tool id — shared so UI routing and registry never drift. */
export const UPDATE_PLAN_TOOL_NAME = 'update_plan' as const;

/**
 * Best-effort parse of update_plan tool input (streaming input may be
 * partial/invalid mid-flight — callers render nothing until it validates).
 */
export function parsePlanInput(input: unknown): UpdatePlanInput | null {
  const result = updatePlanInputSchema.safeParse(input);
  return result.success ? result.data : null;
}
